import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Box, IconButton, Tooltip, Avatar, Menu, MenuItem, Divider, Chip } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import ScienceIcon from '@mui/icons-material/Science';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PersonIcon from '@mui/icons-material/Person';
import Logo from './Logo';
import TabsToolbar from './calculator/TabsToolbar';
import { useAuth } from '../contexts/AuthContext';
import SettingsPanel from './SettingsPanel';
import { useTranslation } from 'react-i18next';

export default function AppToolbar({ region, setRegion, language, setLanguage, hasGlobalSettings, onOpenGlobalSettings, onRegionChangeRequest, onOpenProfile }) {
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { user, logout, isAdmin, isSuperadmin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = () => {
    setUserMenuAnchor(null);
    logout();
  };

  const handleOffersHistory = () => {
    setUserMenuAnchor(null);
    navigate('/offers-history');
  };

  const handleUsersManagement = () => {
    setUserMenuAnchor(null);
    navigate('/admin/users');
  };

  const handleCurrencyRates = () => {
    setUserMenuAnchor(null);
    navigate('/currency-rates');
  };

  const getRoleChip = (role) => {
    const config = {
      superadmin: { label: 'Super Admin', color: '#dc2626', bg: '#fef2f2' },
      admin: { label: 'Admin', color: '#7c3aed', bg: '#f5f3ff' },
      sales: { label: 'Sales', color: '#059669', bg: '#ecfdf5' },
      customer_success: { label: 'CS', color: '#2563eb', bg: '#eff6ff' },
      user: { label: 'User', color: '#6b7280', bg: '#f9fafb' }
    };
    return config[role] || config.user;
  };

  return (
    <AppBar position="static" elevation={0} sx={{ bgcolor: '#fff', color: 'text.primary', boxShadow: '0 1px 8px 0 rgba(60,60,60,0.04)' }}>
      <Toolbar sx={{ minHeight: 56, px: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Logo height={36} />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.primary', ml: 1 }}>
            Ringostat
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flex: 1, minWidth: 0, mx: 2 }}>
          <TabsToolbar />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Global Settings">
            <IconButton
              onClick={onOpenGlobalSettings}
              sx={{
                color: hasGlobalSettings ? '#00C853' : 'rgba(0, 0, 0, 0.54)',
                bgcolor: hasGlobalSettings ? 'rgba(0, 200, 83, 0.1)' : 'transparent',
                '&:hover': {
                  bgcolor: hasGlobalSettings ? 'rgba(0, 200, 83, 0.2)' : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {user && (
            <>
              <SettingsPanel 
                region={region}
                setRegion={setRegion}
                language={language}
                setLanguage={setLanguage}
                onRegionChangeRequest={onRegionChangeRequest}
              />
              <Tooltip title={user.name || 'User'}>
                <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ p: 0 }}>
                  <Avatar src={user.photo_base64 || user.google_picture_url || user.picture} sx={{ width: 32, height: 32 }}>
                    {!(user.photo_base64 || user.google_picture_url || user.picture) && (user.name ? user.name.charAt(0).toUpperCase() : 'U')}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                anchorEl={userMenuAnchor}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    minWidth: 220,
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle1" noWrap sx={{ fontWeight: 'bold' }}>
                    {user.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {user.email}
                  </Typography>
                  {user.role && user.role !== 'user' && (() => {
                    const chip = getRoleChip(user.role);
                    return (
                      <Chip
                        label={chip.label}
                        size="small"
                        sx={{
                          mt: 0.5,
                          height: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          color: chip.color,
                          bgcolor: chip.bg,
                          borderRadius: 1
                        }}
                      />
                    );
                  })()}
                </Box>
                <Divider />
                {onOpenProfile && (
                  <MenuItem onClick={() => { setUserMenuAnchor(null); onOpenProfile(); }} sx={{ py: 1.25, px: 2 }}>
                    <PersonIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <Typography>{t('profile.menuItem', 'My Profile')}</Typography>
                  </MenuItem>
                )}
                <MenuItem onClick={handleOffersHistory} sx={{ py: 1.25, px: 2 }}>
                  <HistoryIcon sx={{ mr: 1.5, color: 'action.active' }} />
                  <Typography>Offers History</Typography>
                </MenuItem>
                <MenuItem onClick={handleCurrencyRates} sx={{ py: 1.25, px: 2 }}>
                  <CurrencyExchangeIcon sx={{ mr: 1.5, color: 'action.active' }} />
                  <Typography>Currency Rates</Typography>
                </MenuItem>
                {isAdmin && (
                  <MenuItem onClick={handleUsersManagement} sx={{ py: 1.25, px: 2 }}>
                    <PeopleIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <Typography>Users</Typography>
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/admin/phone-catalog'); }} sx={{ py: 1.25, px: 2 }}>
                    <ContactPhoneIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <Typography>Phone Catalog</Typography>
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/admin/voice-packages'); }} sx={{ py: 1.25, px: 2 }}>
                    <PhoneInTalkIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <Typography>Voice Packages</Typography>
                  </MenuItem>
                )}
                {isSuperadmin && (
                  <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/test-scenarios'); }} sx={{ py: 1.25, px: 2 }}>
                    <ScienceIcon sx={{ mr: 1.5, color: 'action.active' }} />
                    <Typography>Test Scenarios</Typography>
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ py: 1.25, px: 2 }}>
                  <LogoutIcon sx={{ mr: 1.5, color: 'action.active' }} />
                  <Typography>Logout</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}
